import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getProperty } from "../fsConfig";
import { eachResult, formatResolveByTimes, getSearchParams, isSort as isSorts, loadIssuesForm, validInt } from '../lib/sharedSearchService';
import { collectGarbage, setHelpPage, setWindowTitle } from '../configTdm';
import { INFO_MODALS, TOO_MANY_MY_ISSUES, TOO_MANY_OPEN_ISSUES, TOO_MANY_SEARCH_RESULTS } from '../defineTdm';
import { IssueService, paramsObject, searchIssuesService } from '../issues/issueService';
import styles from './issues.module.css';
import BatchResolve from '../issues/modalBatchResolve';
import BatchAssign from '../issues/modalBatchAssign';
import BatchComment from '../issues/modalBatchComment';
import { Informational } from '../lib/ModalService';
import _ from 'lodash';
import Accordion, { AccordionPanelProps } from "../components/accordion/Accordion";
import Template from '../issues/Template';
import ProgressModal from '../issues/batchProgress';
import { spinnerShow } from '../lib/spinner';
import IssueIcon from '../issues/issueIcon';
import { ErrorState } from '../issues/types';
import { InformationalProps, Issue } from './interfaces';
import FullPageLoadingSpinner from '../../js/pages/components/fullPageLoadingSpinner';

const IssueSearch: React.FC = () => {
  const [totalIssuesFound, setTotalIssuesFound] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [limit, setLimit] = useState<number>(100);
  const [usernames, setUsernames] = useState<{ [key: number]: string }>({});
  const [lastSearchStatus, setLastSearchStatus] = useState<number | null>(null);
  const [assistOpenInNewTab, setAssistOpenInNewTab] = useState<boolean>(false);
  const [issues, setIssues] = useState<Issue[]>([]);
  const [selectedIssues, setSelectedIssues] = useState<Issue[]>([]);
  const [title, setTitle] = useState<string>('');
  const [filtersOn, setFiltersOn] = useState<boolean>(true);
  const [resolveByMaxSetDefaultTime, setResolveByMaxSetDefaultTime] = useState<boolean>(false);
  const [sort, setSort] = useState<string>('time');
  const [sortAsc, setSortAsc] = useState<boolean>(true);
  const [searching, setSearching] = useState<boolean>(false);
  const pollTimer = useRef<NodeJS.Timeout | null>(null);
  const [routeParams, setRouteParams] = useState<{ [key: string]: string }>({});
  const [error, setError] = useState<ErrorState | null>(null);
  const [isDropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [action, setAction] = useState<string>('');
  const [batchActIssues, setBatchActIssues] = useState<Issue[]>([]);
  const [informational, setInformational] = useState<InformationalProps | {}>({});
  const [actions, setActions] = useState<string[]>(['RESOLVE', 'ASSIGN', 'COMMENT']);
  const [loading, setLoading] = useState<boolean>();

  const hasIssues = useMemo(() => totalIssuesFound > 0, [totalIssuesFound]);

  const hasMoreIssues = useMemo(
    () => offset + limit < totalIssuesFound,
    [offset, limit, totalIssuesFound]
  );

  const isMyIssues = () => {
    return (location.pathname + location.search).indexOf('/issues/my') === 0;
  };

  const isOpenIssues = () => {
    return (location.pathname + location.search).indexOf('/issues/open') === 0;
  };

  const isSort = (sortCriteria: string, order?: string) => {
    return isSorts({ sort, sortAsc }, sortCriteria, order);
  };

  const noResultsMessage = useMemo(() => {
    if (lastSearchStatus === 422) {
      if (isMyIssues()) {
        return TOO_MANY_MY_ISSUES;
      } else if (isOpenIssues()) {
        return TOO_MANY_OPEN_ISSUES;
      }
      return TOO_MANY_SEARCH_RESULTS;
    }
    return 'No issues found';
  }, [lastSearchStatus]);

  const evaluateSelectedIssues = () => {
    if (selectedIssues.length) {
      stopPolling();
    } else {
      pollForData();
    }
  };

  const allIssuesSelected = () => {
    return issues.length === selectedIssues.length;
  };

  const selectAllIssues = () => {
    let newSelectedState = !allIssuesSelected();
    const updatedIssues = issues.map((issue) => ({
      ...issue,
      selected: newSelectedState,
    }));
    setSelectedIssues(newSelectedState ? updatedIssues : []);
    setIssues(updatedIssues);
    evaluateSelectedIssues();
  };

  const batchAction = (action: string) => {
    setDropdownOpen(false);
    if (action) {
      const selected = selectedIssues.length ? selectedIssues : issues.filter((issue) => issue.selected);
      const batchIssues = selected.map((selectedIssue) => ({
        ...selectedIssue,
        id: selectedIssue.id,
        tripId: selectedIssue.tripId,
        tripGroupId: selectedIssue.tripGroupId,
        recordLocator: selectedIssue.recordLocator,
      }));
      setBatchActIssues(batchIssues);
      stopPolling();
      switch (action.toLowerCase()) {
        case 'resolve':
          setAction('resolve');
          break;
        case 'assign':
          setAction('assign');
          break;
        case 'comment':
          setAction('comment');
          break;
      }
    }
  };

  const toggleDropdown = () => {
    setDropdownOpen((prevState) => !prevState);
  };

  const sortBy = (sortCriteria: string) => {
    if (!hasIssues) {
      return;
    }

    if (selectedIssues.length) {
      setInformational({
        title: INFO_MODALS.batchSortByWarning.title,
        message1: INFO_MODALS.batchSortByWarning.message,
        useCancelButton: true,
        sortCriteria: sortCriteria,
        flag: 'informational',
        onSubmit: () => {
          setSelectedIssues([]);
          sortBySearch((informational as InformationalProps).sortCriteria || 'time');
          setInformational({});
        },
        cancel: () => setInformational({})
      });
    } else {
      sortBySearch(sortCriteria);
    }
  };

  const sortBySearch = (sortCriteria: string) => {
    stopPolling();

    const form = document.querySelector('[name=searchform]');
    if (form && form.classList.contains('ng-dirty')) {
      loadIssuesForm(routeParams);
    }

    const column = sort;
    let selectedSort = sort;
    let selectedSortAsc = sortAsc;

    if (sortCriteria === column) {
      selectedSortAsc = !sortAsc;
    } else {
      selectedSort = sortCriteria;
      selectedSortAsc = true;
    }

    setOffset(0);
    setIssues([]);
    setTotalIssuesFound(0);
    setLimit(100);

    let urlParams: string[] = [];
    Object.entries({ ...routeParams, ...paramsObject() }).forEach(([key, value]) => {
      switch (key.toLowerCase()) {
        case 'sortby':
        case 'order':
        case 'offset':
          break;
        case 'state':
          if (!isMyIssues() && !isOpenIssues()) {
            urlParams.push(`${key}=${value}`);
          }
          break;
        case 'assignee':
          if (!isMyIssues()) {
            urlParams.push(`${key}=${value}`);
          }
          break;
        default:
          urlParams.push(`${key}=${value}`);
          break;
      }
    });

    if (isMyIssues()) {
      urlParams.push(`state=${encodeURIComponent('open,assisting')}`);
      urlParams.push(`assignee=${getProperty('session', {}).name}`);
    } else if (isOpenIssues()) {
      urlParams.push('state=open');
    }

    urlParams.push(`sortBy=${selectedSort}`);
    urlParams.push(`order=${selectedSortAsc ? 'ascending' : 'descending'}`);
    urlParams.push('offset=0');

    routeParams.sortBy = selectedSort;
    routeParams.order = selectedSortAsc ? 'ascending' : 'descending';

    setSort(selectedSort);
    setSortAsc(selectedSortAsc);
    setRouteParams(routeParams);
    search(urlParams.join('&'));
  };

  const getUsername = (userId: number) => {
    if (usernames?.[userId]) {
      return usernames[userId];
    } else {
      return userId;
    }
  };

  const search = async (params: string, append = false) => {
    if (searching) return;

    setSearching(true);
    setLoading(true);
    try {
      const resp = await searchIssuesService.fetch(params);
      if (resp?.status != null) {
        setLastSearchStatus(resp.status);

        if (resp.status === 200) {
          if (resp.data) {
            if (resp.data?.issues?.length) {
              if (append) {
                setUsernames((prevUsernames) => ({
                  ...prevUsernames,
                  ...resp.data.usernames,
                }));
                setIssues(issues.concat(
                  eachResult(resp.data.issues, [
                    formatResolveByTimes,
                  ]),
                ));
              } else {
                setUsernames(resp.data.usernames);
                setIssues(eachResult(resp.data.issues, [
                  formatResolveByTimes,
                ]));
              }
            }

            if (resp.data?.totalIssuesFound != null) {
              setTotalIssuesFound(validInt(
                resp.data.totalIssuesFound,
              ));
            }

            if (resp.data?.offset != null) {
              setOffset(validInt(resp.data.offset));
            }

            if (resp.data?.limit != null) {
              setLimit(validInt(resp.data.limit));
            }
          }
        }
      }

      pollForData();
    } catch (error) {
      console.error('Error during search:', error);
    } finally {
      setSearching(false);
      setLoading(false);
    }
  };

  const showMoreIssues = () => {
    if (!searching) {
      if (selectedIssues.length > 0) {
        setInformational({
          title: INFO_MODALS.batchShowMoreWarning.title,
          message1: INFO_MODALS.batchShowMoreWarning.message,
          useCancelButton: true,
          flag: 'informational',
          onSubmit: () => {
            const updatedIssues = selectedIssues.map((issue) => ({ ...issue, selected: false }));
            setSelectedIssues([]);
            showMoreIssuesSearch();
          },
          cancel: () => setInformational({})
        });
      } else {
        showMoreIssuesSearch();
      }
    }
  };

  const showMoreIssuesSearch = () => {
    stopPolling();
    let totOffset = offset + limit
    setOffset(totOffset);

    if (totOffset < totalIssuesFound) {
      routeParams.offset = totOffset.toString();
      routeParams.limit = limit.toString();
      routeParams.sortBy = sort;
      routeParams.order = sortAsc ? 'ascending' : 'descending';
      if (isMyIssues()) {
        routeParams.assignee = getProperty('session', {}).name;
        routeParams.state = encodeURIComponent('open,assisting');
      } else if (isOpenIssues()) {
        routeParams.state = 'open';
      }
      let urlParams = getSearchParams(routeParams);
      setRouteParams(routeParams);
      if (urlParams.length) {
        search(urlParams.join('&'), true);
      }
    } else {
      setOffset(totalIssuesFound);
    }
  };

  const assistIssue = useCallback((tripId: number, issueId: number, openInNewTabWindow: boolean) => {
    if (tripId && issueId) {
      IssueService.changeState(
        [{ id: issueId, tripId: tripId } as any],
        'ASSISTING',
      ).then((resp) => {
        spinnerShow();
        if (
          resp?.status === 200 &&
          resp?.data?.requests != null &&
          resp?.data?.succeeded != null &&
          resp.data.requests === resp.data.succeeded
        ) {
          if (openInNewTabWindow) {
            window.open(`/trip/${tripId}#issue-${issueId}`);
            setAssistOpenInNewTab(false);
          } else {
            window.location.href = `/trip/${tripId}#issue-${issueId}`;
          }
        }
      });
    }
  }, []);

  const issuesUpdated = useCallback((data: any) => {
    let infoMessage = INFO_MODALS.batchComplete.messageSuccess;
    let infoMessagePt2 = null;

    if (data?.failed) {
      infoMessage = (INFO_MODALS.batchComplete.messageFailed ?? 'Failed to process %FAILED% out of %TOTAL% requests')
        .replace('%FAILED%', data.failed)
        .replace('%TOTAL%', data.requests);
    }

    if (data?.failedItems?.length) {
      infoMessagePt2 = data?.messaging
        ? INFO_MODALS.batchComplete.failedRecordLocatorsMessaging +
        _.uniq(data.failedItems).join(', ')
        : INFO_MODALS.batchComplete.failedRecordLocators +
        _.uniq(data.failedItems).join(', ');
    }

    setInformational({
      title: INFO_MODALS.batchComplete.title,
      message1: infoMessage,
      useCancelButton: false,
      message2: infoMessagePt2,
      flag: 'informational',
      onSubmit: () => history.go(0),
      cancel: () => history.go(0)
    });
  }, []);

  const pollForData = () => {
    let urlParams: string[] = [];
    if (isMyIssues()) {
      urlParams.push(`assignee=${getProperty('session', {}).name}`);
      urlParams.push(`state=${encodeURIComponent('open,assisting')}`);
    } else if (isOpenIssues()) {
      urlParams.push('state=open');
    }

    if (urlParams.length && !pollTimer.current) {
      urlParams.push(`sortBy=${routeParams.sortBy || 'time'}`);
      urlParams.push(`order=${routeParams.order || 'ascending'}`);

      pollTimer.current = setInterval(() => {
        search(urlParams.join('&'));
      }, 60 * 1000);
    }
  }

  const stopPolling = () => {
    if (pollTimer.current) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }

  useEffect(() => {
    updateTitle();
    searchApply();
    return () => {
      stopPolling();
      setActions([]);
      setUsernames({});
      setIssues([]);
      setSelectedIssues([]);
      collectGarbage();
    };
  }, []);

  const updateTitle = () => {
    let title = 'SEARCH ISSUES';
    let helpPage = 'helpIssueSearch';

    if (isMyIssues()) {
      title = 'MY ISSUE LIST';
      helpPage = 'helpMyIssues';
    } else if (isOpenIssues()) {
      title = 'OPEN ISSUE LIST';
      helpPage = 'helpOpenIssues';
    }

    setTitle(title);
    setWindowTitle(title);
    setHelpPage(helpPage);
  }

  const searchApply = () => {
    const urlParams = getSearchParams(paramsObject());
    let urlParamsJoined = '';

    if (isMyIssues()) {
      urlParams.push(`assignee=${getProperty('session', {}).name}`);
      urlParams.push(`state=${encodeURIComponent('open,assisting')}`);
      setFiltersOn(false);
    } else if (isOpenIssues()) {
      urlParams.push('state=open');
      setFiltersOn(false);
    } else {
      setFiltersOn(true);
    }

    if (urlParams.length) {
      if (!routeParams.sortBy) {
        urlParams.push(`sortBy=${sort}`);
        urlParams.push(`order=${sortAsc ? 'ascending' : 'descending'}`);
      }
      urlParamsJoined = urlParams.join('&');

      if (routeParams.tooMany) {
        setLastSearchStatus(422);
      } else {
        const urlParamsObject = Object.fromEntries(
          urlParams.map((param) => {
            const [key, value] = param.split('=');
            return [key, value];
          })
        );
        setRouteParams(urlParamsObject);
        search(urlParamsJoined);
      }
    }
  }

  let headerLists = [
    { keyName: 'time', displayName: 'Resolve By' },
    { keyName: 'type', displayName: 'Type' },
    { keyName: 'record', displayName: 'Record Loc.' },
    { keyName: 'group', displayName: 'Group Name' },
    { keyName: 'state', displayName: 'Status' },
    { keyName: 'assignee', displayName: 'Assigned To', condition: !isMyIssues() },
    { keyName: 'flight', displayName: 'Flight' },
    { keyName: 'departure', displayName: 'Depart' },
    { keyName: 'arrive', displayName: 'Arrive' },
    { keyName: 'leg', displayName: 'Leg' }
  ];

  let list: Issue[] = [];
  if (issues.length) {
    list = issues;
  }

  const setAssistHover = (selectIndex: number, flag: boolean) => {
    let updatedList = list.map((issue, index) => {
      if (selectIndex === index) {
        return { ...issue, assistHover: flag };
      }
      return { ...issue, assistHover: false };
    });
    if (selectedIssues.length) {
      setSelectedIssues(updatedList);
    } else if (issues.length) {
      setIssues(updatedList);
    }
  }

  const reset = () => {
    setIssues([]);
    setTotalIssuesFound(0);
  }

  let selectedTemplate: any = <></>;
  if ((informational as InformationalProps).flag === 'informational') {
    selectedTemplate = <Informational {...(informational as InformationalProps)}
      onSubmit={() => {
        setSelectedIssues([]);
        sortBySearch((informational as InformationalProps).sortCriteria || 'time');
        setInformational({});
      }} cancel={() => setInformational({})} />;
  } else if (action === 'resolve') {
    selectedTemplate = <BatchResolve cancel={() => { setAction(''); pollForData(); }} issues={batchActIssues} issuesUpdated={issuesUpdated} />;
  } else if (action === 'assign') {
    selectedTemplate = <BatchAssign cancel={() => { setAction(''); pollForData(); }} issues={batchActIssues} issuesUpdated={issuesUpdated} loadSpinner={(caller: boolean) => setLoading(caller)} />;
  } else if (action === 'comment') {
    selectedTemplate = <BatchComment cancel={() => { setAction(''); pollForData(); }} issues={batchActIssues} issuesUpdated={issuesUpdated} />;
  }

  return (
    <>
      {loading && <FullPageLoadingSpinner />} {/* Conditionally render the spinner */}
      {selectedTemplate}
      <div className="row search-title">
        <div className="col-md-12">{title}</div>
      </div>
      {filtersOn &&
        <div className="row">
          <div className="col-md-12 search">
            <div className="row">
              <div className="col-md-12 search-form">
                <Accordion id={"issueSearch"} className='uib-accordion' panels={[{
                  id: 'parent',
                  title: "ISSUE SEARCH CRITERIA",
                  show: true,
                  children: <Template isMyIssues={isMyIssues} isOpenIssues={isOpenIssues} updateTitle={updateTitle} searchApply={searchApply} resetIssues={reset} />
                } as AccordionPanelProps]} />
              </div>
            </div>
          </div>
        </div>
      }
      <div className="row search-results">
        <div className="col-md-12">
          {hasIssues ?
            <div className="total-found">
              <button
                type="button"
                className="btn dropdown-toggle button-action"
                onClick={selectAllIssues}
              >
                ALL
              </button>
              <span className={"btn-group " + (isDropdownOpen ? 'open' : '')}>
                <button
                  type="button"
                  className="btn dropdown-toggle button-action"
                  disabled={list.filter((lines) => lines.selected).length === 0}
                  onClick={toggleDropdown}
                >
                  BATCH ACTIONS <span className="caret"></span>
                </button>
                <ul className="dropdown-menu">
                  {actions.map((action) => (
                    <li key={action} className="batch-option">
                      <a onClick={() => batchAction(action)}>{action}</a>
                    </li>
                  ))}
                </ul>
              </span>
              <span className={styles.xAxisPadding}>
                DISPLAYING 1-{issues.length} of {totalIssuesFound}
              </span>
              {hasMoreIssues && (
                <button
                  type="button"
                  className="btn btn-default button-action"
                  onClick={showMoreIssues}
                >
                  SHOW MORE
                </button>
              )}
            </div> : <div className="total-found">
              <span>NO ITEMS TO DISPLAY</span>
            </div>}
          <div className="table-responsive">
            <table className="table table-striped table-hover search-results-table no-highlight">
              <thead>
                <tr>
                  {headerLists.map((line) => {
                    let conditionFlag = (line.condition === undefined || line.condition === null) ? true : line.condition;
                    let className = (isSort(line.keyName) ? ' sort ' : ' ');
                    if (line.keyName === 'time') {
                      className += " top-left-header-cell ";
                    }
                    if (conditionFlag) {
                      return <th key={line.keyName}
                        className={className}
                        onClick={() => sortBy(line.keyName)}
                      >
                        {line.displayName}
                        <i
                          className={`sprites ${isSort(line.keyName, 'asc')
                            ? 'sprite-arrow-orange-up'
                            : isSort(line.keyName, 'desc')
                              ? 'sprite-arrow-orange-down'
                              : ''
                            }`}
                        ></i>
                      </th>
                    } else {
                      return <React.Fragment key={line.keyName}></React.Fragment>;
                    }
                  })}
                  <th className="top-right-header-cell no-sort">
                    {isOpenIssues() && <span>Actions</span>}
                  </th>
                </tr>
              </thead>
              <tbody>
                {hasIssues && <>
                  {list.map((issue, index) => (
                    <tr key={issue.id} onClick={(e) => {
                      let getLists = [...list];
                      if (e.shiftKey) {
                        // Handle Shift + Click
                        const lastSelectedIndex = getLists.findIndex((line) => line.selected);
                        const currentIndex = index;

                        if (lastSelectedIndex !== -1) {
                          const start = Math.min(lastSelectedIndex, currentIndex);
                          const end = Math.max(lastSelectedIndex, currentIndex);

                          // Select all rows in the range
                          getLists = getLists.map((line, childIndex) => {
                            if (childIndex >= start && childIndex <= end) {
                              return { ...line, selected: true };
                            }
                            return line;
                          });
                        }
                      } else if (e.ctrlKey || e.metaKey) {
                        // Handle Ctrl + Click (or Cmd + Click on macOS)
                        getLists = getLists.map((line, childIndex) => {
                          if (childIndex === index) {
                            return { ...line, selected: !line.selected };
                          }
                          return line;
                        });
                      } else {
                        getLists = list.map((line: any, childIndex: number) => {
                          if (childIndex === index) {
                            return { ...line, selected: true };
                          }
                          return { ...line, selected: false };
                        });
                      }
                      setSelectedIssues(getLists.filter((line: any) => line.selected));
                      setIssues(getLists);
                      evaluateSelectedIssues();
                    }} data-selection-model
                      data-selection-model-mode="multiple" className={issue.selected ? 'selected' : ''}>
                      <td
                        unselectable="on"
                        data-fs-exclude
                        className={`${issue.resolveByTimeIsCritical ? 'critical' : ''} fs-exclude`}
                      >
                        {issue.resolveByTimeFormatted}
                      </td>
                      <td>
                        <IssueIcon type={issue.type} resolveByTime={issue.resolveByTime}></IssueIcon>
                      </td>
                      <td data-fs-exclude className="fs-exclude">
                        <a
                          className="record-locator-base"
                          data-selection-model-ignore
                          href={`/trip/${issue.tripId}#issue-${issue.id}`}
                          onClick={() => { spinnerShow(); }}
                        >
                          {(issue.recordLocator ?? '').length > 10
                            ? `${(issue.recordLocator ?? '').slice(0, 10)}...`
                            : issue.recordLocator}
                        </a>
                      </td>
                      <td unselectable="on" data-fs-exclude className="fs-exclude">
                        {issue.tripGroupName}
                      </td>
                      <td unselectable="on">
                        {issue.state}
                      </td>
                      {!isMyIssues() && <td unselectable="on" data-fs-exclude className="fs-exclude">
                        {getUsername(issue.assigneeUserId as any)}
                      </td>}
                      <td unselectable="on">
                        {issue.airlineCode} {issue.flightNumber}
                      </td>
                      <td unselectable="on">
                        {issue.departureAirportCode}
                      </td>
                      <td unselectable="on">
                        {issue.arrivalAirportCode}
                      </td>
                      {issue?.legSequence?.number ? <td unselectable="on">
                        {issue?.legSequence?.number} of {issue?.legSequence?.of}
                      </td> : <td unselectable="on"></td>}
                      <td data-selection-model-ignore>
                        {isOpenIssues() && (
                          <div className="action-bar">
                            <div
                              className={`sprites action ${issue.assistHover ? 'sprite-assist-selected-24' : 'sprite-assist-normal-24'
                                }`}
                              title="Assist"
                              onMouseEnter={() => setAssistHover(index, true)}
                              onMouseLeave={() => setAssistHover(index, false)}
                              onClick={() => assistIssue(issue.tripId as any, issue.id as any, assistOpenInNewTab)}
                              data-assist-open-in-new-tab={assistOpenInNewTab}
                              data-assist-issue-context-menu
                            ></div>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </>}
              </tbody>
            </table>
          </div>
          {hasIssues && <>
            <div className="separator"></div>
            <div className="total-found">
              <span>DISPLAYING 1-{issues.length} of {totalIssuesFound}</span>
              {hasMoreIssues && (
                <button
                  type="button"
                  className="btn btn-default button-action"
                  onClick={showMoreIssues}
                >
                  SHOW MORE
                </button>
              )}
            </div>
          </>}
        </div>
        {!hasIssues && (
          <div className="row">
            <div className="col-md-12">
              <div className="col-md-12 no-search-results">
                {noResultsMessage}
              </div>
            </div>
          </div>
        )}
        {error && (
          <div className="row">
            <div className="col-md-12">
              <div className="col-md-12 error">
                {error.title} - {error.message}
              </div>
            </div>
          </div>
        )}
      </div>
      <ProgressModal></ProgressModal>
    </>
  );
};

export default IssueSearch;